import type { LicenceDeliveryDto, LockState } from '@his/shared';
import { Badge } from '@/components/ui/primitives';
import { dateTime } from '@/lib/utils';

const REJECTION_TEXT: Record<string, string> = {
  MALFORMED: 'the licence was damaged in transit',
  NOT_CANONICAL: 'the licence was damaged in transit',
  WRONG_PRODUCT: 'the licence is for another product',
  UNKNOWN_KEY: 'the server does not trust the signing key — install a current release',
  BAD_SIGNATURE: 'the signature did not verify',
  UNSIGNED: 'the licence was not signed',
  NO_BAKED_KEY: 'the server was built without its licence key',
  WRONG_TENANT: 'the licence is for another facility',
  WRONG_FINGERPRINT: 'the licence is for another machine',
  NOT_NEWER: 'the server already holds a newer licence',
};

const DAY_MS = 24 * 60 * 60 * 1000;

function minutesAgo(iso: string | null, now: number): string {
  if (!iso) return 'never';
  const minutes = Math.max(0, Math.round((now - Date.parse(iso)) / 60_000));
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `${hours} h ago` : `${Math.round(hours / 24)} days ago`;
}

export function licenceDeliveryText(
  licence: LicenceDeliveryDto,
  context: { lastCheckInAt: string | null; lockState: LockState; now?: number },
): { label: string; variant: 'success' | 'warning' | 'destructive' | 'muted' } {
  const now = context.now ?? Date.now();
  if (licence.state === 'applied') return { label: 'Applied', variant: 'success' };
  if (licence.state === 'legacy') return { label: 'Old server version — update to receive licences', variant: 'muted' };
  if (licence.state === 'rejected') {
    const reason = licence.rejectedReason?.split(':')[1] ?? '';
    return { label: `Rejected: ${REJECTION_TEXT[reason] ?? reason}`, variant: 'destructive' };
  }
  if (context.lockState !== 'NONE') return { label: 'Server read-only — applies when it checks in', variant: 'warning' };
  return { label: `Waiting for the facility's server — last check-in ${minutesAgo(context.lastCheckInAt, now)}`, variant: 'warning' };
}

export function LicenceDeliveryPill({
  licence,
  lastCheckInAt,
  lockState,
}: {
  licence: LicenceDeliveryDto | null | undefined;
  lastCheckInAt: string | null;
  lockState: LockState;
}) {
  if (!licence) return <span className="text-xs text-muted-foreground">—</span>;
  const text = licenceDeliveryText(licence, { lastCheckInAt, lockState });
  const signatureGap =
    licence.lastSignatureOkAt !== null && lastCheckInAt !== null && Date.parse(lastCheckInAt) - Date.parse(licence.lastSignatureOkAt) > DAY_MS;
  return (
    <span className="inline-flex flex-col gap-0.5">
      <Badge variant={text.variant}>{text.label}</Badge>
      {signatureGap && licence.lastSignatureOkAt && (
        <span className="text-[11px] text-warning">Unsigned answers since {dateTime(licence.lastSignatureOkAt)}</span>
      )}
    </span>
  );
}
